import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, X, BellOff } from "lucide-react";

const STORAGE_KEY = "npc_hide_transition_popup_v2";

export default function TransitionPopup() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        // Show once per site access, unless the user chose to hide it permanently.
        let hidden = false;
        try {
            hidden = localStorage.getItem(STORAGE_KEY) === "1";
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("TransitionPopup: could not read localStorage", e);
            }
            hidden = false;
        }
        if (!hidden) {
            const t = setTimeout(() => setOpen(true), 1200);
            return () => clearTimeout(t);
        }
    }, []);

    // Dismiss for this visit only — it will appear again next time.
    const dismissForNow = () => setOpen(false);

    // Hide permanently on this device.
    const hideForever = () => {
        try {
            localStorage.setItem(STORAGE_KEY, "1");
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("TransitionPopup: could not persist preference", e);
            }
        }
        setOpen(false);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="transition-popup"
                    role="dialog"
                    aria-live="polite"
                    aria-label="Practice name transition notice"
                    initial={{ opacity: 0, y: 40, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 40, scale: 0.96 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="fixed bottom-5 right-5 z-[60] w-[calc(100vw-2.5rem)] max-w-sm overflow-hidden rounded-2xl border border-amber-400/30 bg-[#211610]/95 shadow-[0_20px_60px_rgba(0,0,0,0.55)] backdrop-blur-md"
                >
                    <div className="h-1 w-full bg-gradient-to-r from-amber-500 via-amber-300 to-amber-500" />
                    <div className="p-5">
                        <button
                            data-testid="transition-popup-close"
                            onClick={dismissForNow}
                            aria-label="Close for now"
                            className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-amber-200/60 transition-colors hover:bg-amber-500/10 hover:text-amber-100"
                        >
                            <X className="h-4 w-4" />
                        </button>

                        <div className="flex items-start gap-3 pr-6">
                            <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/15 ring-1 ring-amber-400/40">
                                <Flame className="h-4.5 w-4.5 text-amber-400" />
                            </span>
                            <div>
                                <p className="font-serif text-[15px] leading-snug text-amber-100">
                                    Same Safe Space, new name
                                </p>
                                <p className="mt-1.5 text-[13px] leading-relaxed text-amber-100/70">
                                    We're now{" "}
                                    <span className="font-semibold text-amber-300">
                                        NeuroPlay Counseling, LLC
                                    </span>{" "}
                                    — same warm room, same care, same team.
                                </p>
                            </div>
                        </div>

                        <div className="mt-4 flex items-center justify-between gap-3">
                            <button
                                data-testid="transition-popup-hide-forever"
                                onClick={hideForever}
                                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-amber-200/60 transition-colors hover:text-amber-100"
                            >
                                <BellOff className="h-3.5 w-3.5" />
                                Hide this pop-up next time
                            </button>
                            <button
                                data-testid="transition-popup-ok"
                                onClick={dismissForNow}
                                className="rounded-full bg-amber-500 px-4 py-1.5 text-[13px] font-semibold text-[#1F1510] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-amber-400"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
